import React from 'react';
import { Popover } from 'antd';
import { StoreResponse } from '@ydistri/api-sdk';
import { ReadOnlyItem, Row, Text } from '@ydistri/ds';
import { useApplicationConfiguration } from '../../../hooks/useApplicationConfiguration';
import Identifications from '../Identifications/Identifications';
import {
  useIdentificationItems,
  UseIdentificationItemsParams,
} from '../Identifications/useIdentificationItems';
import { NO_DATA_PLACEHOLDER } from '../NoData/NoData';

interface StoreIdentificationsProps {
  store: Pick<StoreResponse, 'id' | 'customerStoreId' | 'code' | 'storeDecile'>;
  systemIdMode?: UseIdentificationItemsParams['systemIdMode'];
  /**
   * Overrides store.storeDecile with the per-calculation SkuAttributeValue snapshot value.
   * Required whenever this component is shown alongside a Brand Quintile for the same SKU: a
   * SKU-detail screen must never mix a "now" decile (dbo.StorePerformance, what store.storeDecile
   * carries) with a historical quintile from the same calculation. Leave undefined for a pure store
   * context (no SKU anchored), where "now" is the correct answer.
   */
  storeDecileOverride?: string | null;
  /**
   * Store x brand x category grain, from the anchored SKU's own side (SourceSku/TargetSku on
   * SkuRedistributionResponse) - never a live StoreCategoryBrandPerformance read. Undefined drops
   * the quintile from the Perf item entirely, for a pure store context.
   */
  brandQuintile?: number | null;
}

const StoreIdentifications: React.FC<StoreIdentificationsProps> = ({
  store,
  systemIdMode,
  storeDecileOverride,
  brandQuintile,
}) => {
  const { displayStoreId } = useApplicationConfiguration();

  const identifications = useIdentificationItems({
    displayId: displayStoreId,
    entityId: store.id,
    entityCode: store.code,
    entityCustomerId: store.customerStoreId,
    systemIdMode: systemIdMode,
  });

  // null/'' covers both "never ranked" and "no matching row" - both show the no-data placeholder,
  // never an empty string and never a manufactured default.
  const storeDecileValue =
    storeDecileOverride !== undefined
      ? // eslint-disable-next-line typescript/prefer-nullish-coalescing -- '' must render as NoData too (SkuAttributeResponse.Selectors.cs collapses "no data" to '' server-side), not just null/undefined
        storeDecileOverride || NO_DATA_PLACEHOLDER
      : (store.storeDecile?.toString() ?? NO_DATA_PLACEHOLDER);

  // brandQuintile reaches this component typed number | null off the SKU side of
  // SkuRedistributionResponse, never through the SkuAttributeValue pivot, so '' cannot arrive here
  // and ?? is correct - null/undefined both mean "no data", never "weakest".
  const brandQuintileValue =
    brandQuintile !== undefined ? (brandQuintile?.toString() ?? NO_DATA_PLACEHOLDER) : undefined;

  const perfValue =
    brandQuintileValue !== undefined
      ? `${storeDecileValue} | ${brandQuintileValue}`
      : storeDecileValue;

  const perfPopover =
    brandQuintileValue !== undefined ? (
      <Row $gap="0.7rem">
        <ReadOnlyItem label="Store Decile" plainLabel={true}>
          <Text $semiBold={true}>{storeDecileValue}</Text>
        </ReadOnlyItem>
        <Text>|</Text>
        <ReadOnlyItem label="Brand Quintile" plainLabel={true}>
          <Text $semiBold={true}>{brandQuintileValue}</Text>
        </ReadOnlyItem>
      </Row>
    ) : (
      <ReadOnlyItem label="Store Decile" plainLabel={true}>
        <Text $semiBold={true}>{storeDecileValue}</Text>
      </ReadOnlyItem>
    );

  return (
    <>
      <Popover content={perfPopover}>
        <Row>
          <ReadOnlyItem label="Perf" value={perfValue} spaceBetween="0.5rem" />
        </Row>
      </Popover>
      <Identifications items={identifications} spaceBetween="0.5rem" />
    </>
  );
};

export default React.memo(StoreIdentifications);
